import { pushBreadcrumb, updateBreadcrumb } from './breadcrumbStore';
import {
  BREADCRUMB_OUTCOME,
  FEEDBACK_ACTION,
  type ActionDetail,
  type FeedbackAction,
} from './ontology/types';

/**
 * Fixed, PII-free labels for action breadcrumbs keyed by the action contract identifier.
 * Labels use a neutral action phrase (not success tense) so a single fixed label reads
 * correctly regardless of the breadcrumb's outcome (pending, success, or failure).
 */
const ACTION_BREADCRUMB_LABELS: Record<FeedbackAction, string> = {
  [FEEDBACK_ACTION.POST_MESSAGE]: 'Post message',
  [FEEDBACK_ACTION.POST_QUESTION]: 'Post question',
  [FEEDBACK_ACTION.POST_SUGGESTED_QUESTION]: 'Post suggested question',
  [FEEDBACK_ACTION.REQUEST_AI_REPLY]: 'Request AI reply',
  [FEEDBACK_ACTION.SAVE_POST]: 'Save post',
  [FEEDBACK_ACTION.UNSAVE_POST]: 'Remove saved post',
  [FEEDBACK_ACTION.SAVE_PRODUCT]: 'Save product',
  [FEEDBACK_ACTION.UNSAVE_PRODUCT]: 'Remove saved product',
  [FEEDBACK_ACTION.ADD_PRODUCT_BY_URL]: 'Add product by URL',
  [FEEDBACK_ACTION.FOLLOW_USER]: 'Follow user',
  [FEEDBACK_ACTION.UNFOLLOW_USER]: 'Unfollow user',
  [FEEDBACK_ACTION.FOLLOW_TOPIC]: 'Follow topic',
  [FEEDBACK_ACTION.UNFOLLOW_TOPIC]: 'Unfollow topic',
  [FEEDBACK_ACTION.CREATE_INVITE]: 'Create invite',
  [FEEDBACK_ACTION.LOG_IN]: 'Log in',
  [FEEDBACK_ACTION.CREATE_ACCOUNT]: 'Create account',
  [FEEDBACK_ACTION.START_OAUTH_SIGN_IN]: 'Start OAuth sign-in',
  [FEEDBACK_ACTION.COMPLETE_OAUTH_SIGN_IN]: 'Complete OAuth sign-in',
  [FEEDBACK_ACTION.LOG_OUT]: 'Log out',
  [FEEDBACK_ACTION.CHANGE_PASSWORD]: 'Change password',
  [FEEDBACK_ACTION.START_EMAIL_CHANGE]: 'Start email change',
  [FEEDBACK_ACTION.RESEND_EMAIL_VERIFICATION]: 'Resend email verification',
  [FEEDBACK_ACTION.CANCEL_EMAIL_CHANGE]: 'Cancel email change',
  [FEEDBACK_ACTION.REQUEST_PASSWORD_RESET]: 'Request password reset',
  [FEEDBACK_ACTION.RESET_PASSWORD]: 'Reset password',
  [FEEDBACK_ACTION.UPDATE_AVATAR]: 'Update avatar',
  [FEEDBACK_ACTION.UPDATE_MARKETPLACE_PREFERENCES]: 'Update marketplace preferences',
  [FEEDBACK_ACTION.UPDATE_PROFILE_DISCOVERABILITY]: 'Update profile discoverability',
  [FEEDBACK_ACTION.REMOVE_OAUTH_CONNECTION]: 'Remove OAuth connection',
  [FEEDBACK_ACTION.DELETE_ACCOUNT]: 'Delete account',
};

/** Handle used to mark an in-flight action breadcrumb as succeeded or failed. */
interface ActionHandle {
  succeeded: (detail?: ActionDetail) => void;
  failed: (detail?: ActionDetail) => void;
}

/** Returns a concise, bounded string reason from an unknown thrown value. */
export function errorReason(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.length <= 200 ? message : `${message.slice(0, 197)}...`;
}

/**
 * Starts an action breadcrumb immediately and returns a handle for its eventual outcome.
 * Callers must guarantee exactly one terminal succeeded()/failed() on every path (unlike traceAction, which guarantees it).
 */
export function beginAction(action: FeedbackAction, detail?: ActionDetail): ActionHandle {
  const breadcrumbId = pushBreadcrumb({
    kind: 'action',
    label: ACTION_BREADCRUMB_LABELS[action],
    outcome: BREADCRUMB_OUTCOME.PENDING,
    detail: { action, ...(detail ?? {}) },
  });

  return {
    succeeded(nextDetail?: ActionDetail) {
      updateBreadcrumb(breadcrumbId, {
        outcome: BREADCRUMB_OUTCOME.SUCCESS,
        detail: nextDetail,
      });
    },
    failed(nextDetail?: ActionDetail) {
      updateBreadcrumb(breadcrumbId, {
        outcome: BREADCRUMB_OUTCOME.FAILURE,
        detail: nextDetail,
      });
    },
  };
}

/** Runs an async action while keeping one breadcrumb updated from pending to success or failure. */
export async function traceAction<T>(
  action: FeedbackAction,
  fn: () => Promise<T>,
  detail?: ActionDetail
): Promise<T> {
  const handle = beginAction(action, detail);

  try {
    const result = await fn();
    handle.succeeded();
    return result;
  } catch (error) {
    handle.failed({ error: errorReason(error) });
    throw error;
  }
}
